import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CheckCircle } from 'lucide-react';

interface FinalizedQuantitiesDisplayProps {
  specifications: Array<{ id: string; specification_name?: string; name?: string }>;
  deliveryLocations: Array<{ id: string; delivery_name: string }>;
  /** Finalized distribution data from job_specification_location_quantities */
  distributionData: Array<{
    specification_id: string;
    delivery_location_id: string;
    quantity: number;
    percentage?: number;
  }>;
  /** Unit labels per specification */
  units?: Record<string, string>;
}

/**
 * Displays finalized distribution quantities in a read-only view
 * Shown after a job has been awarded and distribution has been confirmed
 */
export function FinalizedQuantitiesDisplay({
  specifications,
  deliveryLocations,
  distributionData,
  units = {}
}: FinalizedQuantitiesDisplayProps) {
  // Group distribution data by specification
  const distributionBySpec = distributionData.reduce((acc, dist) => {
    if (!acc[dist.specification_id]) {
      acc[dist.specification_id] = {};
    }
    acc[dist.specification_id][dist.delivery_location_id] = {
      quantity: dist.quantity,
      percentage: dist.percentage
    };
    return acc;
  }, {} as Record<string, Record<string, { quantity: number; percentage?: number }>>);

  // Calculate totals per specification
  const getTotalForSpec = (specId: string): number => {
    const specData = distributionBySpec[specId];
    if (!specData) return 0;
    return Object.values(specData).reduce((sum, item) => sum + (item.quantity || 0), 0);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span className="font-medium">Distribution Confirmed</span>
        </div>
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          Finalized at Award
        </Badge>
      </div>

      <p className="text-sm text-muted-foreground">
        These quantities were confirmed when the job was awarded and will be used for the purchase order.
      </p>

      <div className="overflow-x-auto border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="min-w-[200px]">Specification</TableHead>
              {deliveryLocations.map(loc => (
                <TableHead key={loc.id} className="text-right min-w-[120px]">
                  {loc.delivery_name}
                </TableHead>
              ))}
              <TableHead className="text-right min-w-[100px] font-semibold">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {specifications.map(spec => {
              const specDistribution = distributionBySpec[spec.id] || {};
              const total = getTotalForSpec(spec.id);
              const unit = units[spec.id] || 'units';

              return (
                <TableRow key={spec.id}>
                  <TableCell className="font-medium">
                    {spec.specification_name || spec.name}
                  </TableCell>
                  {deliveryLocations.map(loc => {
                    const locData = specDistribution[loc.id];
                    const quantity = locData?.quantity || 0;
                    const percentage = locData?.percentage;

                    return (
                      <TableCell key={loc.id} className="text-right">
                        <div className="flex flex-col items-end">
                          <span className="font-medium">
                            {quantity.toLocaleString()} {unit}
                          </span>
                          {percentage !== undefined && percentage > 0 && (
                            <span className="text-xs text-muted-foreground">
                              ({percentage}%)
                            </span>
                          )}
                        </div>
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-right font-semibold">
                    {total.toLocaleString()} {unit}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
